import { LicensePlan } from '../types/license'

/** Commercial packs shown in the product UI. */
export type CommercialPackKey = 'standard' | 'business' | 'premium'

export type PackFeatureStatus =
  | 'included'
  | 'business'
  | 'premium'
  | 'soon'
  | 'soonBusiness'
  | 'soonPremium'

export type PackFeature = {
  id: string
  /** i18n key under license.packFeatures.* */
  labelKey: string
  status: PackFeatureStatus
}

/**
 * Pack content for Standard / Business / Premium.
 * Shared rows are listed once; higher tiers inherit lower-tier features.
 */
export const PACK_FEATURES: PackFeature[] = [
  // All packs
  { id: 'maturity', labelKey: 'maturityScore', status: 'included' },
  { id: 'frameworks', labelKey: 'frameworks', status: 'included' },
  { id: 'unlimitedUsers', labelKey: 'unlimitedUsers', status: 'included' },
  { id: 'hosting', labelKey: 'hosting', status: 'included' },
  { id: 'emailSupport', labelKey: 'emailSupport', status: 'included' },
  // Business + Premium
  { id: 'actionPlan', labelKey: 'actionPlan', status: 'business' },
  { id: 'roadmap', labelKey: 'roadmap', status: 'business' },
  { id: 'opsModules', labelKey: 'opsModules', status: 'business' },
  { id: 'mapping', labelKey: 'mapping', status: 'business' },
  { id: 'aiEvidence', labelKey: 'aiEvidence', status: 'business' },
  { id: 'integrations', labelKey: 'integrations', status: 'business' },
  // Premium
  { id: 'rgpd', labelKey: 'rgpdModule', status: 'premium' },
  { id: 'onboarding', labelKey: 'onboarding', status: 'premium' },
  { id: 'phoneSupport', labelKey: 'phoneSupport', status: 'premium' },
  { id: 'consulting', labelKey: 'consulting', status: 'premium' },
  // Coming soon
  { id: 'aiRemediation', labelKey: 'aiRemediation', status: 'soonBusiness' },
  { id: 'api', labelKey: 'api', status: 'soonBusiness' },
  { id: 'reportLogo', labelKey: 'reportLogo', status: 'soonPremium' },
]

export const COMMERCIAL_PACKS: {
  key: CommercialPackKey
  /** Backend plan keys that map onto this commercial pack */
  backendKeys: LicensePlan[]
  accent: { bg: string; text: string; border: string }
}[] = [
  {
    key: 'standard',
    backendKeys: ['basic'],
    accent: { bg: '#eef2f7', text: '#00146d', border: '#c5d3e8' },
  },
  {
    key: 'business',
    backendKeys: ['business'],
    accent: { bg: '#e8f0fb', text: '#1b2f8f', border: '#a8c0e8' },
  },
  {
    key: 'premium',
    backendKeys: ['premium'],
    accent: { bg: '#e1f4f0', text: '#076b5c', border: '#c5e4dc' },
  },
]

export function toCommercialPack(
  planKey: LicensePlan | string | null | undefined
): CommercialPackKey {
  if (planKey === 'premium') return 'premium'
  if (planKey === 'business') return 'business'
  return 'standard'
}

export function packToBackendKey(pack: CommercialPackKey): LicensePlan {
  if (pack === 'premium') return 'premium'
  if (pack === 'business') return 'business'
  return 'basic'
}

export function featureIncludedInPack(
  feature: PackFeature,
  pack: CommercialPackKey
): boolean {
  switch (feature.status) {
    case 'included':
    case 'soon':
      return true
    case 'business':
    case 'soonBusiness':
      return pack === 'business' || pack === 'premium'
    case 'premium':
    case 'soonPremium':
      return pack === 'premium'
    default:
      return false
  }
}

export function featureIsSoon(feature: PackFeature): boolean {
  return (
    feature.status === 'soon' ||
    feature.status === 'soonBusiness' ||
    feature.status === 'soonPremium'
  )
}
